import cc from "classcat"
import { usePopper } from "react-popper"
import { Placement } from "@popperjs/core"
import { Icon } from "./Icon"
import {
  useState,
  useEffect,
  Children,
  Fragment,
  ReactNode,
  cloneElement,
  ReactElement,
  HTMLProps,
} from "react"

interface Props {
  placement?: Placement
  content: ReactNode
  children: ReactElement
  className?: string
  show?: boolean
  triggerWith?: "hover" | "click" | null
  onClose?: () => void
  id: string
}

export const Tooltip = ({
  placement = "top",
  show: isShowingProp = false,
  triggerWith = "hover",
  children,
  content,
  className,
  onClose,
  id,
}: Props) => {
  const [referenceElement, setReferenceElement] = useState<Element | null>(null)
  const [tooltipElement, setTooltipElement] = useState<HTMLElement | null>(null)
  const [arrowElement, setArrowElement] = useState<HTMLElement | null>(null)

  const [isShowing, setShowing] = useState(isShowingProp)
  const show = () => setShowing(true)
  const hide = () => setShowing(false)
  const toggle = () => setShowing((isShowing) => !isShowing)

  const referenceId = `tooltip-reference__${id}`
  const tooltipId = `tooltip__${id}`

  const { styles, attributes } = usePopper(referenceElement, tooltipElement, {
    placement,
    modifiers: [
      { name: "arrow", options: { element: arrowElement } },
      { name: "offset", options: { offset: [0, 8] } },
    ],
  })

  useEffect(() => {
    if (onClose && !isShowing) onClose()
  }, [isShowing, onClose])

  useEffect(() => {
    setShowing(isShowingProp)
  }, [isShowingProp])

  const tooltipProps: HTMLProps<HTMLElement> = {
    ref: setTooltipElement,
    role: "tooltip",
    id: tooltipId,
    className: cc([
      className,
      "tooltip",
      { "tooltip--closeable": triggerWith === "click" },
    ]),
    style: styles.popper,
  }

  const referenceElementProps: HTMLProps<Element> = {
    ref: setReferenceElement,
    id: referenceId,
    tabIndex: 0,
    "aria-describedby": tooltipId,
  }

  if (triggerWith === "hover") {
    referenceElementProps.onMouseEnter = show
    referenceElementProps.onFocus = show
    referenceElementProps.onMouseLeave = hide
    referenceElementProps.onBlur = hide
  }

  if (triggerWith === "click") {
    referenceElementProps.onClick = toggle
    referenceElementProps.onKeyDown = (e) => {
      if (e.key === "Enter") toggle()
    }
  }

  return (
    <Fragment>
      {cloneElement(Children.only(children), referenceElementProps)}

      <span data-show={isShowing} {...tooltipProps} {...attributes.popper}>
        {content}
        {triggerWith === "click" && (
          <button
            className="btn btn--small btn--text-inverted tooltip__close"
            aria-label="Close"
            onClick={() => setShowing(false)}
          >
            <Icon name="x" />
          </button>
        )}
        <span
          ref={setArrowElement}
          role="presentation"
          className="tooltip__arrow"
          style={styles.arrow}
        />
      </span>
    </Fragment>
  )
}
